<template>
  <t-rich-select
    ref="botSelect"
    :key="refreshBotSelect"
    :options="botOptions"
    value-attribute="id"
    text-attribute="name"
    :value="selectedBotId"
    :open-on-focus="true"
    variant="botSelector"
    max-height="400"
    :hide-search-box="true"
    :close-on-select="true"
    @mouseover.native="updateHover(true)"
    @mouseleave.native="updateHover(false)"
    @blur="isDropdownOpen = false"
    @click="isDropdownOpen = !isDropdownOpen"
    @input="changeBot"
  >
    <template #option="{ option }">
      <div class="p-2">{{ option.text }}</div>
    </template>

    <template #label>
      <with-loader id="bot-selector"
                   :loading="botsLoading"
                   use-as-overlay
                   :class="{ 'w-full': botsLoading }"
                   class="flex items-center gap-2"
      >

        <bot-icon :class="getColorClass()"/>
        <div class="flex-1 min-w-0 truncate">
          {{ selectedBotLabel }}
        </div>
      </with-loader>
    </template>

    <template #arrow>
      <sort-desc-icon class="transform transition-transform duration-500 text-primary background-primary"
                      :class="{ 'rotate-180': isDropdownOpen, 'hidden': botsLoading }"
                      order="desc"
                      :is-active="false"
      />
    </template>
  </t-rich-select>
</template>

<script>
import BotIcon from '@/assets/images/icons/BotIcon'
import botModule from '@/store/modules/bot.module'
import Template from '@/components/templates/Template'
import SortDescIcon from '@/components/common/SortableTableArrowsSort'
import sort from 'ramda/src/sort'
import WithLoader from '@/components/common/WithLoader'

export default {
  name: 'BotSelector',

  components: { Template, BotIcon, SortDescIcon, WithLoader },

  props: {
    defaultBotId: {
      type: String
    },
    optionsFilter: {
      type: Function,
      default: o => o
    },
    getBotLabel: {
      type: Function,
      default: null
    },
    exactBotId: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isHover: false,
      isDropdownOpen: false,
      refreshBotSelect: 0
    }
  },

  methods: {
    getColorClass() {
      return this.isHover ? 'text-primary' : 'text-gray-dark'
    },

    updateHover(isHover) {
      this.isHover = isHover
    },

    changeBot(botId) {
      this.isDropdownOpen = false
      if (!botId || this.selectedBotId === botId) return

      const bot = this.bots.find(bot => bot.id === botId)
      this.$emit('change', bot)

      // if change of bot fails, set an original bot and close dropdown
      if (!this.exactBotId && this.selectedBotId !== botId) {
        const primaryBot = botModule.resolvePrimaryBot(botModule.getBot()?.id)
        if (primaryBot) this.$refs.botSelect.selectOption(primaryBot)
      }

      // this throws js error inside npm package, because for a brief moment this.$refs.button (there) is undefined
      // it doesn't seem any negative manifestation, need because of bot change after change dialog popup
      this.refreshBotSelect = Math.random()
    },

    // Point the store's current bot at the route's bot. syncBotById REPLACES it (BOT_SET) when the
    // bot id changes and no-ops when it is already current — unlike a shallow merge, which would keep
    // stale fields from the previous bot (e.g. a multi-market variant's primary_bot_id) and make the
    // picker keep showing the old bot after navigating to a different one.
    syncSelectedBot() {
      if (this.exactBotId) {
        const bot = this.bots.find(b => b.id === this.defaultBotId)
        if (bot && botModule.getBot()?.id !== bot.id) {
          botModule.setBot(bot)
        }
        return
      }

      botModule.syncBotById(this.defaultBotId)
    }
  },

  computed: {
    bots: botModule.getBotList,
    botsLoading: botModule.getBotLoading,

    selectedBotId() {
      if (this.exactBotId) {
        return this.defaultBotId || botModule.getBotId()
      }

      return botModule.getPrimaryBotId()
    },

    botOptions() {
      const sorter = (a, b) => a.name.localeCompare(b.name)
      const filteredBots = this.bots.filter(b => this.optionsFilter(b))
      const labeledBots = this.getBotLabel ?
        filteredBots.map(bot => ({ ...bot, name: this.getBotLabel(bot) })) :
        filteredBots

      return sort(sorter, labeledBots)
    },

    selectedBotLabel() {
      const selectedOption = this.botOptions.find(bot => bot.id === this.selectedBotId)
      if (selectedOption) return selectedOption.name

      const selectedBot = this.bots.find(bot => bot.id === this.selectedBotId)
      if (!selectedBot) return ''

      return this.getBotLabel ? this.getBotLabel(selectedBot) : selectedBot.name
    }
  },
  watch: {
    // Follow route-driven bot changes (e.g. opening a variable usage on a different bot) even when
    // the selector itself is not re-mounted.
    defaultBotId() {
      this.syncSelectedBot()
    },

    // Direct navigation (e.g. email-template for a market variant) can mount this component before
    // the bot list is populated; the initial syncSelectedBot() then no-ops and the store stays on
    // the primary bot until the list arrives.
    bots() {
      if (this.exactBotId) {
        this.syncSelectedBot()
      }
    }
  },

  mounted() {
    this.syncSelectedBot()
  }
}
</script>

<style lang="scss">
#bot-selector {
  .effects {
    @apply border-gray;
    border-left: 3px solid #030D41 !important;
  }
}
</style>
